import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class MicrosoftErrorService {

  private readonly errorMap: { codes: string[], message: string, title: string }[] = [
    // Credentials and secrets — only support team can resolve
    {
      codes: ['AADSTS7000222', 'AADSTS7000215'],
      title: 'Configuration error',
      message: 'Microsoft sign-in is temporarily unavailable due to an expired application credential. Please contact itopia support to resolve this issue.'
    },
    // Invalid redirect URI — only support can resolve
    {
      codes: ['AADSTS50011'],
      title: 'Configuration error',
      message: 'Microsoft sign-in is not properly configured for this application. Please contact itopia support.'
    },
    // App not authorized in tenant — admin can resolve from Azure
    {
      codes: ['AADSTS700016', 'AADSTS650052'],
      title: 'App not authorized',
      message: 'This application has not been authorized in your Microsoft tenant. Please visit the Azure portal to grant admin consent, or contact itopia support for assistance.'
    },
    // Tenant not found — admin should verify configuration
    {
      codes: ['AADSTS90002', 'AADSTS900023'],
      title: 'Tenant not found',
      message: 'Your Microsoft organization tenant could not be found. Please verify your Azure tenant configuration or contact itopia support.'
    },
    // Access blocked by conditional access policy — admin should review policies
    {
      codes: ['AADSTS53003', 'AADSTS53000', 'AADSTS53001'],
      title: 'Access blocked',
      message: 'Access has been blocked by a conditional access policy in your Microsoft tenant. Please review your Azure Active Directory policies or contact itopia support.'
    },
    // Access denied by user
    {
      codes: ['AADSTS65004', 'AADSTS650053'],
      title: 'Access denied',
      message: 'You declined the permission request. Please try signing in again and accept the required permissions to continue.'
    },
    // Account does not belong to tenant
    {
      codes: ['AADSTS50020'],
      title: 'Wrong account',
      message: 'This Microsoft account does not belong to the expected organization. Please sign in with your organization admin account.'
    },
    // Account disabled
    {
      codes: ['AADSTS50057'],
      title: 'Account disabled',
      message: 'Your Microsoft admin account has been disabled. Please contact your Microsoft tenant administrator.'
    },
    // Password expired
    {
      codes: ['AADSTS50055'],
      title: 'Password expired',
      message: 'Your Microsoft password has expired. Please reset your password and try again.'
    },
    // MFA required or failed
    {
      codes: ['AADSTS50076', 'AADSTS50079', 'AADSTS50074'],
      title: 'Verification required',
      message: 'Multi-factor authentication is required or failed. Please complete the Microsoft verification steps and try again.'
    },
    // Token / session expired
    {
      codes: ['AADSTS70011', 'AADSTS70008', 'AADSTS700082'],
      title: 'Session expired',
      message: 'Your Microsoft session has expired. Please sign in again.'
    },
    // User not found
    {
      codes: ['AADSTS50034'],
      title: 'Account not found',
      message: 'Your Microsoft account was not found. Please verify you are using the correct admin email address.'
    },
    // Interactive sign-in required
    {
      codes: ['AADSTS16000', 'AADSTS16001'],
      title: 'Sign-in required',
      message: 'An interactive sign-in is required. Please sign in again.'
    },
  ];

  /**
   * Determines if the error message is a Microsoft OAuth/AADSTS error.
   */
  isMicrosoftError(message: string): boolean {
    if (!message) return false;
    return message.includes('OAuth2') ||
           message.includes('AADSTS') ||
           message.includes('error_description=');
  }

  /**
   * Returns a sanitized, user-friendly message and title from a raw Microsoft error.
   * Returns null if it is not a Microsoft error.
   */
  sanitize(rawMessage: string): { title: string; message: string } | null {
    if (!this.isMicrosoftError(rawMessage)) return null;

    const descMatch = rawMessage.match(/error_description=([^&}]+)/);
    if (descMatch) {
      const decoded = decodeURIComponent(descMatch[1].replace(/\+/g, ' '));
      const mapped = this.mapByCode(decoded);
      if (mapped) return mapped;

      // Fallback: strip AADSTS prefix and take the first sentence
      const cleaned = decoded.replace(/AADSTS\d+:\s*/, '').split('.')[0].trim();
      return {
        title: 'Authentication error',
        message: cleaned + '.'
      };
    }

    return null;
  }

  private mapByCode(decoded: string): { title: string; message: string } | null {
    for (const entry of this.errorMap) {
      if (entry.codes.some(code => decoded.includes(code))) {
        return { title: entry.title, message: entry.message };
      }
    }
    return null;
  }
}